import React, { useState, useMemo, useCallback } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import { filterData, formatValue } from '@ts-core/utils/dataTableUtils';

import TSTable from '@ts-core/components/Table/TSTable';
import { TSTableColumn } from '@ts-core/components/Table/TSTable/TSTable';

import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import QuestionStatsPopover from 'modules/subjects/components/QuestionStatsPopover';

import AnswerTypeIcon from '@ts-core/components/AnswerTypeIcon';

import { SubjectWithStats, SubjectQuestionWithAnswer, SubjectExerciseWithStats } from '../..//types/stats';

interface SubjectStatsTableProps {
  subject?: SubjectWithStats;
  loading: boolean;
  error?: string;

  onClickExercise: (exercise: SubjectExerciseWithStats) => void;
}

const SubjectStatsTable = (props: SubjectStatsTableProps) => {
  const intl = useIntl();
  const [query, setQuery] = useState('');
  const [popoverData, setPopoverData] = useState({});

  const { subject, loading, error, onClickExercise } = props;

  const { name, score, students, exercises, totalAnswers } = subject || {};

  const onOpenQuestionPopOver = useCallback((event: React.MouseEvent, question: SubjectQuestionWithAnswer) => {
    const { nameWithExercise, answerTypes, totalAnswers: answersCount } = question;
    setPopoverData({
      questionData: {
        title: nameWithExercise,
        answersCount,
        answerTypes,
      },
      anchorEl: event.target,
    });
  }, []);

  const onCloseQuestionPopover = useCallback(() => {
    setPopoverData({});
  }, []);

  const getQuestionHeadCell = useCallback(
    (exercise: SubjectExerciseWithStats, question: SubjectQuestionWithAnswer) => {
      return (
        <Link onClick={() => onClickExercise(exercise)}>
          <Typography
            aria-haspopup="true"
            variant="subtitle2"
            onMouseEnter={(event) => onOpenQuestionPopOver(event, question)}
            onMouseLeave={onCloseQuestionPopover}
          >
            {question.name}
          </Typography>
        </Link>
      );
    },
    [onClickExercise, onOpenQuestionPopOver, onCloseQuestionPopover]
  );

  const columns = useMemo((): TSTableColumn<SubjectWithStats>[] => {
    const result: TSTableColumn<SubjectWithStats>[] = [
      {
        Header: intl.formatMessage({ id: 'assignmentResultsTable.student' }),
        value: intl.formatMessage({ id: 'assignmentResultsTable.student' }),
        Footer: intl.formatMessage({ id: 'averages' }),
        accessor: 'name',
        sortable: true,
      },
    ];

    if (students?.some((s) => s.group)) {
      result.push({
        Header: intl.formatMessage({ id: 'assignmentResultsTable.group' }),
        value: intl.formatMessage({ id: 'assignmentResultsTable.group' }),
        accessor: 'group',
        id: 'group',
        sortable: true,
        filterable: true,
      });
    }

    result.push({
      Header: intl.formatMessage({ id: 'result' }),
      value: intl.formatMessage({ id: 'result' }),
      accessor: 'percentageScore',
      sortable: true,
      valueTransformer: (value) => formatValue(value, 1),
      Footer: formatValue(score, 1, true, 1),
    });

    exercises?.forEach((e) => {
      const subColumns = e.questions.map((q) => ({
        Header: getQuestionHeadCell(e, q),
        value: `${e.code} - ${q.name}`,
        Footer: formatValue(q.answerTypes ? q.answerTypes.positive : 0, totalAnswers),
        valueTransformer: (value: any) => <AnswerTypeIcon type={value} />,
        accessor: `answers.${q.id}`,
      }));

      result.push({
        Header: e.code,
        columns: subColumns,
      });
    });

    return result;
  }, [totalAnswers, exercises, getQuestionHeadCell, students, intl, score]);

  const filterKeys = ['name', 'group'];
  const filteredStudents = filterData(students, query, filterKeys);

  return (
    <>
      <TSTable
        columns={columns}
        data={filteredStudents}
        loading={loading}
        errorMessage={error}
        onSearch={setQuery}
        allowDownloadAsXLSX
        emptyMessage={<FormattedMessage id="subjectStatsTable.emptyMessage" />}
        xlsxFileName={name}
      />
      <QuestionStatsPopover {...popoverData} onClose={onCloseQuestionPopover} />
    </>
  );
};

export default SubjectStatsTable;
